import React, { useState, useCallback, useEffect } from 'react';

import Modal from 'components/Modal/Modal';
import {
    Button, Text, Link, Flex, Icon
} from 'components/ui';
import OtpInputGroup from 'components/OtpInputGroup';
import OtpTimer from 'components/GlobalModals/EmailVerificationModal/OtpTimer/OtpTimer';

import { wrapFunctionalComponent } from 'utils/framework';
import { colors } from 'style/config';
import { EMAIL_OTP_ERROR_CODES } from 'constants/phoneVerification';
import isFunction from 'utils/functions/isFunction';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import linkTrackingError from 'analytics/bindings/pages/all/linkTrackingError';
import anaUtils from 'analytics/utils';

function EmailVerificationModal({
    isOpen = false,
    onDismiss,
    localization = {},
    showInfoModal,
    sendEmailOTP,
    checkOTP,
    onMFASuccess,
    email,
    analyticsData = {}
}) {
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [resendCode, setResendCode] = useState(false);
    const [resetTimer, setResetTimer] = useState(false);
    const [error, setError] = useState(null);

    // AC1: Fire TrackState when user lands on the Email verification landing screen
    useEffect(() => {
        if (isOpen && !showOtpInput) {
            // ACS-2685 AC4: Fire anlytics after user clicks "Send to Email" button
            const previousAsyncPageLoadData = {
                ...(analyticsData && {
                    linkData: analyticsData.linkData ?? '', // prop55
                    previousPageName: analyticsData.previousPageName ?? '' // prop6
                })
            };

            processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                data: {
                    ...anaUtils.getLastAsyncPageLoadData(),
                    pageName: `${anaConsts.PAGE_TYPES.USER_PROFILE}:${anaConsts.PAGE_DETAIL.EMAIL_VERIFICATION}:${anaConsts.PAGE_DETAIL.EMAIL_VERIFICATION_LANDING}:*`,
                    pageType: anaConsts.PAGE_TYPES.USER_PROFILE,
                    pageDetail: anaConsts.PAGE_DETAIL.EMAIL_VERIFICATION,
                    ...previousAsyncPageLoadData
                }
            });
        }
    }, [isOpen, showOtpInput]);

    // AC3: Fire TrackState when user lands on the email verification OTP screen
    useEffect(() => {
        if (isOpen && showOtpInput) {
            processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                data: {
                    ...anaUtils.getLastAsyncPageLoadData(),
                    pageName: `${anaConsts.PAGE_TYPES.USER_PROFILE}:${anaConsts.PAGE_DETAIL.EMAIL_VERIFICATION}:${anaConsts.PAGE_DETAIL.EMAIL_VERIFICATION_VERIFICATION}:*`,
                    pageType: anaConsts.PAGE_TYPES.USER_PROFILE,
                    pageDetail: anaConsts.PAGE_DETAIL.EMAIL_VERIFICATION,
                    // Intentionally reset these props on subsequent calls
                    linkData: '', // prop55
                    previousPageName: anaUtils.getLastAsyncPageLoadData().pageName // prop6
                }
            });
        }
    }, [isOpen, showOtpInput]);

    const onShowInfo = useCallback(() => {
        showInfoModal({
            title: localization.infoTitle,
            message: localization.infoMessage,
            buttonText: localization.infoOk
        });
    }, [showInfoModal]);

    const handleOnSendEmailOtp = useCallback(async () => {
        // AC2: Fire TrackAction after user clicks "Send Email Verification" button
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                pageName: `${anaConsts.PAGE_TYPES.USER_PROFILE}:${anaConsts.PAGE_DETAIL.EMAIL_VERIFICATION}:${anaConsts.PAGE_DETAIL.EMAIL_VERIFICATION_LANDING}:*`,
                actionInfo: anaConsts.LinkData.EMAIL_VERIFICATION_SEND
            }
        });

        try {
            await sendEmailOTP();
            setShowOtpInput(true);
        } catch (err) {
            Sephora.logger.verbose('Error sending Email OTP', err);

            // Fire TrackState for Email error modal
            processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                data: {
                    ...anaUtils.getLastAsyncPageLoadData(),
                    pageName: `${anaConsts.PAGE_TYPES.USER_PROFILE}:${anaConsts.PAGE_DETAIL.EMAIL_VERIFICATION}:${anaConsts.PAGE_DETAIL.EMAIL_VERIFICATION_ERROR}:*`,
                    pageType: anaConsts.PAGE_TYPES.USER_PROFILE,
                    pageDetail: anaConsts.PAGE_DETAIL.EMAIL_VERIFICATION,
                    prop28: anaConsts.FIELD_ERRORS.BI_VERIFICATION,
                    prop48: localization.infoErrorMessage
                }
            });
            showInfoModal({
                title: localization.infoErrorTitle,
                message: localization.infoErrorMessage,
                buttonText: localization.infoOk,
                callback: onDismiss
            });
        }
    }, [sendEmailOTP, onDismiss, showInfoModal, localization]);

    const handleResendOtp = useCallback(async () => {
        clearError();

        // AC4: Fire TrackAction after user clicks "Resend" button
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                pageName: `${anaConsts.PAGE_TYPES.USER_PROFILE}:${anaConsts.PAGE_DETAIL.EMAIL_VERIFICATION}:${anaConsts.PAGE_DETAIL.EMAIL_VERIFICATION_VERIFICATION}:*`,
                actionInfo: anaConsts.LinkData.EMAIL_VERIFICATION_RESEND
            }
        });

        try {
            await sendEmailOTP();
            setResendCode(false);
            setResetTimer(true);
        } catch (err) {
            // Fire TrackState for Email error modal (resend)
            processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                data: {
                    ...anaUtils.getLastAsyncPageLoadData(),
                    pageName: `${anaConsts.PAGE_TYPES.USER_PROFILE}:${anaConsts.PAGE_DETAIL.EMAIL_VERIFICATION}:${anaConsts.PAGE_DETAIL.EMAIL_VERIFICATION_ERROR}:*`,
                    pageType: anaConsts.PAGE_TYPES.USER_PROFILE,
                    pageDetail: anaConsts.PAGE_DETAIL.EMAIL_VERIFICATION,
                    prop28: anaConsts.FIELD_ERRORS.BI_VERIFICATION,
                    prop48: localization.infoErrorMessage
                }
            });
            showInfoModal({
                title: localization.infoErrorTitle,
                message: localization.infoErrorMessage,
                buttonText: localization.infoOk
            });
        }
    }, [sendEmailOTP, showInfoModal, localization]);

    const handleCheckOtp = useCallback(
        async otpCode => {
            clearError();

            try {
                const response = await checkOTP(otpCode);

                // ACS-2693 AC5: Fire TrackAction after user successfully verifies email
                processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                    data: {
                        actionInfo: anaConsts.LinkData.EMAIL_VERIFICATION_VERIFIED,
                        eventStrings: ['event3']
                    }
                });

                if (isFunction(onMFASuccess)) {
                    onMFASuccess(response);
                }
            } catch (err) {
                switch (err?.errorCode) {
                    case EMAIL_OTP_ERROR_CODES.INVALID_CODE:
                        setError(localization.errorOtpIncorrect);
                        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                            data: {
                                bindingMethods: linkTrackingError,
                                fieldErrors: [anaConsts.FIELD_ERRORS.BI_VERIFICATION],
                                errorMessages: [localization.errorOtpIncorrect]
                            }
                        });

                        break;

                    case EMAIL_OTP_ERROR_CODES.ERR_OTP_VERIFY_EXPIRED:
                        setError(localization.expiredOTP);
                        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                            data: {
                                bindingMethods: linkTrackingError,
                                fieldErrors: [anaConsts.FIELD_ERRORS.BI_VERIFICATION],
                                errorMessages: [localization.expiredOTP]
                            }
                        });

                        break;
                    case EMAIL_OTP_ERROR_CODES.TOO_MANY_ATTEMPTS:
                        // Fire TrackState for email OTP "Too Many Attempts" modal
                        processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                            data: {
                                ...anaUtils.getLastAsyncPageLoadData(),
                                pageName: `${anaConsts.PAGE_TYPES.USER_PROFILE}:${anaConsts.PAGE_DETAIL.EMAIL_OTP}:${anaConsts.PAGE_DETAIL.EMAIL_OTP_TOO_MANY_ATTEMPTS}:*`,
                                pageType: anaConsts.PAGE_TYPES.USER_PROFILE,
                                pageDetail: anaConsts.PAGE_DETAIL.EMAIL_OTP
                            }
                        });
                        showInfoModal({
                            title: localization.infoErrorTooManyAttemptsTitle,
                            message: localization.infoErrorTooManyAttemptsMessage,
                            buttonText: localization.infoOk,
                            callback: onDismiss
                        });

                        break;
                    default:
                        setError(localization.errorGenericMessage);
                        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                            data: {
                                bindingMethods: linkTrackingError,
                                fieldErrors: [anaConsts.FIELD_ERRORS.BI_VERIFICATION],
                                errorMessages: [localization.errorGenericMessage]
                            }
                        });

                        break;
                }
            }
        },
        [checkOTP, onMFASuccess, showInfoModal, localization]
    );

    const clearError = () => setError(null);

    const handleOnTimeout = () => {
        setResendCode(true);
    };

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={onDismiss}
            isDrawer={false}
            width={showOtpInput ? 0 : 1}
        >
            <Modal.Header hasBorder>
                <Modal.Title>{localization.modalTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {showOtpInput ? (
                    <Flex
                        flexDirection='column'
                        gap={4}
                    >
                        <Flex
                            flexDirection='column'
                            gap={[2, null, 4]}
                        >
                            <Text
                                is='h2'
                                fontSize='md'
                                fontWeight='bold'
                                lineHeight='20px'
                                children={localization.step2Title}
                            />
                            <Text
                                is='p'
                                lineHeight='18px'
                                dangerouslySetInnerHTML={{
                                    __html: localization.step2MessageLine1
                                }}
                            />
                        </Flex>
                        {error && (
                            <Text
                                is='p'
                                lineHeight='18px'
                                color={colors.error}
                                children={error}
                            />
                        )}
                        <OtpInputGroup
                            onComplete={handleCheckOtp}
                            error={error}
                            clearError={clearError}
                        />
                        <Text is='p'>
                            {resendCode ? (
                                <>
                                    {localization.step2MessageLine2}{' '}
                                    <Button
                                        variant='link'
                                        padding={0}
                                        minHeight={0}
                                        minWidth={0}
                                        onClick={handleResendOtp}
                                        children={localization.resend}
                                    />
                                </>
                            ) : (
                                <>
                                    {localization.step2MessageLine2} {localization.resend} {localization.step2MessageLine2Part2}{' '}
                                    <OtpTimer
                                        localization={localization}
                                        onTimeoutCallback={handleOnTimeout}
                                        resetTimer={resetTimer}
                                    />
                                </>
                            )}
                        </Text>
                    </Flex>
                ) : (
                    <Flex
                        flexDirection='column'
                        gap={2}
                    >
                        <Text
                            is='h2'
                            fontSize='md'
                            fontWeight='bold'
                            lineHeight='20px'
                            children={localization.step1Title}
                        />
                        <Flex
                            flexDirection='column'
                            gap={4}
                        >
                            <Text
                                is='p'
                                lineHeight='18px'
                            >
                                {localization.step1MessageLine1}{' '}
                                <Text
                                    role='button'
                                    aria-label={localization.accessibilityInfoIcon}
                                >
                                    <Icon
                                        name='infoOutline'
                                        size={16}
                                        color='gray'
                                        onClick={onShowInfo}
                                    />
                                </Text>
                            </Text>
                            <Text
                                is='p'
                                lineHeight='18px'
                            >
                                <Text children={localization.step1MessageLine2} />
                                <Text
                                    display='block'
                                    fontWeight='bold'
                                    children={email}
                                />
                            </Text>
                            <Text
                                is='p'
                                fontSize='sm'
                                color='gray'
                                lineHeight='14px'
                            >
                                {localization.step1MessageLine3}{' '}
                                <Link
                                    color='blue'
                                    fontWeight='bold'
                                    href='/beauty/sms-terms-and-conditions'
                                    children={localization.termsOfUse}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    underline
                                />{' '}
                                {localization.step1MessageLine3Part2}{' '}
                                <Link
                                    color='blue'
                                    fontWeight='bold'
                                    href='/beauty/privacy-policy'
                                    children={localization.privacyPolicy}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    underline
                                />
                                {'.'}
                            </Text>
                        </Flex>
                    </Flex>
                )}
            </Modal.Body>
            {showOtpInput || (
                <Modal.Footer hasBorder={false}>
                    <Button
                        variant='primary'
                        width='100%'
                        children={localization.send}
                        onClick={handleOnSendEmailOtp}
                    />
                </Modal.Footer>
            )}
        </Modal>
    );
}

export default wrapFunctionalComponent(EmailVerificationModal, 'EmailVerificationModal');
